import React, { useEffect, useMemo, useState } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import * as Joi from "joi";

import { Dispatch } from 'redux'
import Loader from '../global/loader';
import { LocationSelect } from '../form/locationSelect';
import { useLoadMetrics } from './hooks/useLoadsMetrics';
import { Select } from '../form/validatedSelect';
import { loadMetricDefinitions, updateTargets, setValidationError, clearValidationError } from '../../store/futureSecurity/actions';
import { MultiSelect } from '../form/validatedMultiSelect';
import { EditMetric, MetricEditingState } from './editMetric';
import { validateRAGSchema } from '../../store/futureSecurity/schema';
import { ViewMetric } from './viewMetric';
import { AuthTenant } from '../../helpers/authentication';

export const FSRThresholds: React.FC = () => {
  const dispatch: Dispatch<any> = useDispatch();

  const { location, setLocation } = useLoadMetrics(dispatch);
  const { terminals, metricDefinitions, lanes, cells, isLoadingTerminals, isLoadingMetricDefinitions } = useSelector(
    (state: MainState) => ({
      terminals: state.futureSecurity.terminals,
      metricDefinitions: state.futureSecurity.metrics,
      lanes: state.futureSecurity.lanes,
      cells: state.futureSecurity.cells,
      errorMessage: state.futureSecurity.validationError,
      isLoadingMetricDefinitions: state.futureSecurity.isLoadingMetricDefinitions,
      isLoadingMetricValues: state.futureSecurity.isLoadingMetricValues,
      isLoadingTerminals: state.futureSecurity.isLoadingTerminals,
    }),
    shallowEqual
  );
  const [terminal, setTerminal] = useState<string>(terminals[0] || "")
  const [metric, setMetric] = useState(metricDefinitions[0])
  const [applyTo, setApplyTo] = useState<{ value: string, label: string }[]>([])
  const [isFallbackValue, setIsFallbackValue] = useState<boolean>(false);
  const [viewMetricActive, setViewMetricActive] = useState<boolean>(false)

  const onSubmit = (metricChangeDetails: MetricEditingState): void => {
    // Clear any existing validation errors
    dispatch(clearValidationError());
    
    const error = validateRAGSchema(metric, metricChangeDetails, isFallbackValue);
    if (error) {
      // Extract the first error message and dispatch as string
      const errorMessage = error.details ? error.details[0].message : (error.message || 'Validation error occurred');
      dispatch(setValidationError(errorMessage));
      return;
    }
    
    dispatch(updateTargets(metric, metricChangeDetails, applyTo.map(lane => lane.value), isFallbackValue, terminal));
  }
  useEffect(() => {
    // Validate that the terminal is attached to the right airport
    const terminalOptions: Record<string, string[]> = {
        "MAN": ["T1A", "T1B", "T2", "T2 Staff"],
        "EMA": ["Central Search", "Staff Channel"],
        "STN": ["Central Search", "Staff Channel", "Forecourt"]
      }
    if(terminalOptions[location]?.includes(terminal))
    dispatch(loadMetricDefinitions(location, terminal))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location, terminal])

  useEffect(() => {
    setMetric(metricDefinitions[0])
  }, [metricDefinitions])

  useEffect(() => {
    setTerminal(terminals[0])
  }, [terminals])


  const getLaneOptions = useMemo(() => {
    let res: { value: string; label: string; }[] = []

    if (metric?.groupType === "cell") {
      const terminalCells = cells.find(
        (cell) => {
          return cell.name.toSnakeCase() === terminal.toSnakeCase() && cell.airport === location
        }
      );
      res = terminalCells ? terminalCells.results.map((cellNumber) => ({ 
        value: cellNumber, 
        label: cellNumber 
      })) : [];
    } else if (metric?.groupType === "lane") {
      const terminalLanes = lanes.find(
        (lane) => {
          return lane.name.toSnakeCase() === terminal.toSnakeCase() && lane.airport === location
        }
      );
      res = terminalLanes ? terminalLanes.results.map((lane) => ({ value: lane, label: lane })) : [];
    }
    if(!isFallbackValue && !viewMetricActive) res.unshift({label: "All", value: "All"})
    return res;
  }, [metric, location, terminal, cells, lanes])

  const changeToDefaultText = isFallbackValue ? "Edit specific values" : "Edit fallback values"

  const onChangeMetric = (value: string) => {
    const selectedMetric = metricDefinitions.find((m) => m.name === value);
    if (selectedMetric) {
      setMetric(selectedMetric);
    }
  }

  useEffect(() => {
    // reset cells or lanes when any top level option changes
    if(viewMetricActive) {
      const nonAllOptions = getLaneOptions.filter(lane => lane.value.toLowerCase() !== "all");
      setApplyTo(nonAllOptions);
    } else {
      setApplyTo([])
    }
  }, [metric, terminal, location, viewMetricActive])

  function handleLaneSelect(v: {label: string, value: string}[]): void {
    const selectionValue = v[v.length - 1];
    console.log(selectionValue)
    if(selectionValue?.value.toLowerCase() === "all") {
      setApplyTo(getLaneOptions!.filter(option => option.value.toLowerCase() !== "all"))
    } else {
      setApplyTo(v)
    }
  }
  
  const showEditComponent = location && (terminals.length > 0 ? terminal : true) && ((!isFallbackValue && applyTo?.length > 0) || isFallbackValue) && !viewMetricActive
  const showViewTimedMetric = location && (terminals.length > 0 ? terminal : true) && viewMetricActive && applyTo.length > 0

  return (
    <main>
      <h1>Metric Management</h1>
      <button style={{ width: "100%" }} disabled={metricDefinitions.length < 1} onClick={() => setViewMetricActive(!viewMetricActive)}>{viewMetricActive ? "Edit targets" : "View specific targets"}</button>
      <LocationSelect value={location} onChange={setLocation} optionsProp='futureSecurityTenants' />
      {isLoadingTerminals ?
        <Loader isLoading={true} /> :
        terminals.length > 1 &&
        <Select
          schema={Joi.string()}
          value={terminal}
          onChange={setTerminal}
          label="Terminal"
          options={terminals.map((term) => ({ label: `${term.replace("T", "Terminal ")}`, value: term }))}
        />
      }
      {isLoadingMetricDefinitions ? <Loader isLoading={true} /> : <><Select
        schema={Joi.string()}
        value={metric?.name ?? ""}
        onChange={onChangeMetric}
        label="Metric"
        options={metricDefinitions?.map((metric: MetricDefinition) => ({ label: metric.name, value: metric.name }))}
      />
        <p style={{ marginTop: "15px", marginBottom: "10px" }}>
          {viewMetricActive ? "You're viewing targets at a specific date and time." : (isFallbackValue ?
            "You're editing targets for the fallback values, which is defaulted to if a specific target is not set. Click to switch back to set specific targets." :
            "You're editing targets for a specific metric at a specific time."
          )}
        </p>
        {/* Disabling ability to edit defaults temporarily */}
        {/* {!viewMetricActive && <button onClick={() => setIsFallbackValue(!isFallbackValue)}>{changeToDefaultText}</button>} */}
        {!isFallbackValue && !viewMetricActive && <MultiSelect
          options={getLaneOptions}
          label="Apply to (Lanes/ Cells)"
          onChange={handleLaneSelect}
          schema={Joi.any()}
          selectedOptions={applyTo || []}
        />}
        {viewMetricActive &&
          <Select
            label="Cell/ Lane"
            onChange={(v) => setApplyTo([{label: v, value: v}])}
            schema={Joi.any()}
            options={getLaneOptions}
            value={applyTo[0]?.value || getLaneOptions[0].value}
          />}
        <hr />
        {showEditComponent && <EditMetric isFallback={isFallbackValue} metric={metric} onSubmit={onSubmit} applyTo={applyTo} terminal={terminal} />}
        {showViewTimedMetric && <ViewMetric metric={metric} terminal={terminal} applyTo={applyTo[0]} />}
      </>
      }
    </main>
  );
}

